import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Columns3, FolderKanban, Layers, List } from 'lucide-react';
import { useProjectIdFromPath } from '../../lib/project-path';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import {
  filtersToSearchParams,
  searchParamsToFilters,
  type DisplaySpec,
  type FilterSpec,
} from '../../lib/filters';
import { cn } from '../../lib/utils';
import { useOverlays } from './overlay-context';
import FilterBar from './FilterBar';

type ViewScope = 'workspace' | 'project';

const DEFAULT_PROPERTIES = {
  key: true,
  state: true,
  priority: true,
  assignee: true,
  labels: true,
  start_date: true,
  due_date: true,
  parent: false,
  created_at: false,
  updated_at: false,
};

function displayForLayout(layout: 'list' | 'kanban', current: DisplaySpec): DisplaySpec {
  return {
    ...current,
    layout,
    group_by: layout === 'kanban' ? 'state' : null,
    order_by: current.order_by ?? (layout === 'kanban' ? 'sort_order' : '-created_at'),
    show_empty_groups: current.show_empty_groups ?? true,
    show_sub_items: current.show_sub_items ?? false,
  };
}

function keepFloatingOpen(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('[data-radix-popper-content-wrapper]')) {
    event.preventDefault();
  }
}

export default function CreateViewModal() {
  const pathProjectId = useProjectIdFromPath();
  const { createView, setCreateViewOpen, toast } = useOverlays();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = createView.projectId ?? pathProjectId;
  const [scope, setScope] = useState<ViewScope>('workspace');
  const [name, setName] = useState('');
  const [layout, setLayout] = useState<'list' | 'kanban'>('list');
  const [filters, setFilters] = useState<FilterSpec>({});
  const [display, setDisplay] = useState<DisplaySpec>({
    layout: 'list',
    group_by: null,
    order_by: '-created_at',
    show_empty_groups: true,
    show_sub_items: false,
  });
  const [busy, setBusy] = useState(false);
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);

  useEffect(() => {
    if (!createView.open) return;
    const parsed = searchParamsToFilters(searchParams);
    const nextScope: ViewScope = 'workspace';
    const nextLayout = 'list';
    setScope(nextScope);
    setName('');
    setBusy(false);
    setLayout(nextLayout);
    setFilters(parsed.filters);
    setDisplay(displayForLayout(nextLayout, {
      order_by: parsed.display.order_by,
      show_empty_groups: parsed.display.show_empty_groups,
      show_sub_items: parsed.display.show_sub_items,
      show_bugs: parsed.display.show_bugs,
    }));
    setMatchCount(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createView.open]);

  useEffect(() => {
    if (!createView.open) {
      setMatchCount(null);
      setCounting(false);
      return;
    }
    if (scope === 'project' && !projectId) {
      setMatchCount(null);
      setCounting(false);
      return;
    }
    let cancelled = false;
    setCounting(true);
    const handle = window.setTimeout(() => {
      const params = filtersToSearchParams(filters, { order_by: display.order_by });
      params.set('limit', '1');
      params.set('offset', '0');
      const path =
        scope === 'project' && projectId
          ? `/projects/${projectId}/work-items`
          : '/work-items';
      void apiFetch<{ page: { total: number } }>(`${v1Path(path)}?${params.toString()}`)
        .then((res) => {
          if (!cancelled) setMatchCount(res.page.total);
        })
        .catch(() => {
          if (!cancelled) setMatchCount(null);
        })
        .finally(() => {
          if (!cancelled) setCounting(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [createView.open, scope, projectId, filters, display.order_by]);

  function chooseLayout(next: 'list' | 'kanban') {
    setLayout(next);
    setDisplay((current) => displayForLayout(next, current));
  }

  function chooseScope(next: ViewScope) {
    setScope(next);
    if (next === 'workspace' && layout === 'kanban') {
      chooseLayout('list');
    }
  }

  function applySpecs(next: { filters: FilterSpec; display: DisplaySpec }) {
    setFilters(next.filters);
    setDisplay(displayForLayout(layout, next.display));
  }

  async function submit() {
    const workspace = scope === 'workspace';
    if (!workspace && !projectId) {
      toast('Open a stream to create a stream view.');
      return;
    }
    const trimmed = name.trim();
    if (!trimmed) return;
    const nextLayout = workspace ? 'list' : layout;
    setBusy(true);
    try {
      const path = workspace ? '/views' : `/projects/${projectId}/views`;
      const view = await apiFetch<{ id: string }>(v1Path(path), {
        method: 'POST',
        body: JSON.stringify({
          name: trimmed,
          layout: nextLayout,
          filters,
          display: {
            ...display,
            layout: nextLayout,
            group_by: nextLayout === 'kanban' ? 'state' : null,
            order_by: display.order_by ?? (nextLayout === 'kanban' ? 'sort_order' : '-created_at'),
            show_empty_groups: display.show_empty_groups ?? true,
            show_sub_items: display.show_sub_items ?? false,
            properties: DEFAULT_PROPERTIES,
          },
          is_shared: true,
        }),
      });
      setCreateViewOpen(false);
      navigate(workspace ? `/views/${view.id}` : `/projects/${projectId}/views/${view.id}`);
    } catch (err) {
      toast(apiErrorMessage(err, 'Failed to create view'));
    } finally {
      setBusy(false);
    }
  }

  const matchLabel = counting
    ? 'Counting matches…'
    : matchCount == null
      ? null
      : matchCount === 0
        ? 'No items match these filters.'
        : matchCount === 1
          ? '1 item matches'
          : `${matchCount} items match`;

  return (
    <Dialog
      open={createView.open}
      onOpenChange={(open) => {
        if (!open) setCreateViewOpen(false);
      }}
    >
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        onPointerDownOutside={keepFloatingOpen}
        onInteractOutside={keepFloatingOpen}
      >
        <DialogHeader>
          <DialogTitle>Create view</DialogTitle>
          <DialogDescription>
            Save a filter you can reopen. Default is every stream you belong to.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Scope</p>
            <div className="grid grid-cols-2 gap-2">
              <LayoutCard
                selected={scope === 'workspace'}
                title="All my streams"
                hint="Work from every stream you belong to"
                onSelect={() => chooseScope('workspace')}
                icon={<Layers className="h-4 w-4" aria-hidden />}
              />
              <LayoutCard
                selected={scope === 'project'}
                title="This stream"
                hint={projectId ? 'Only this stream’s items' : 'Open a stream first'}
                onSelect={() => chooseScope('project')}
                disabled={!projectId}
                icon={<FolderKanban className="h-4 w-4" aria-hidden />}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="view-name">Name</Label>
            <Input
              id="view-name"
              value={name}
              autoFocus
              placeholder="Bugs this week"
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void submit();
                }
              }}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">Layout</p>
            <div className="grid grid-cols-2 gap-2">
              <LayoutCard
                selected={layout === 'list'}
                title="List"
                hint="A sortable table"
                onSelect={() => chooseLayout('list')}
                icon={<List className="h-4 w-4" aria-hidden />}
              />
              <LayoutCard
                selected={layout === 'kanban'}
                title="Board"
                hint={scope === 'workspace' ? 'Available on a single stream' : 'Columns by state'}
                onSelect={() => chooseLayout('kanban')}
                disabled={scope === 'workspace'}
                icon={<Columns3 className="h-4 w-4" aria-hidden />}
              />
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <p className="text-sm font-medium">Filters</p>
              <p className="text-xs text-muted-foreground">
                {scope === 'workspace'
                  ? 'Matches across every stream you belong to. Leave empty to include everything.'
                  : 'Same filters as the board. Leave them empty to include everything.'}
              </p>
            </div>
            <FilterBar
              projectId={scope === 'project' ? projectId : undefined}
              filters={filters}
              display={display}
              onChange={applySpecs}
              onClear={() => applySpecs({ filters: {}, display })}
            />
          </div>
        </div>
        <DialogFooter className="gap-3 sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground sm:text-left">{matchLabel ?? '\u00a0'}</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setCreateViewOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={busy || !name.trim()} onClick={() => void submit()}>
              {busy ? 'Creating…' : 'Create view'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LayoutCard({
  selected,
  title,
  hint,
  icon,
  onSelect,
  disabled = false,
}: {
  selected: boolean;
  title: string;
  hint: string;
  icon: ReactNode;
  onSelect: () => void;
  disabled?: boolean;
}) {
  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    if (disabled) return;
    onSelect();
  }
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'flex items-start gap-3 rounded-lg border px-3 py-3 text-left transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : selected
            ? 'border-primary bg-primary/5'
            : 'hover:bg-muted/70',
      )}
    >
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <span>
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </button>
  );
}
